import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Printer, Search, Plus, Trash2, ChevronRight, Loader2 } from 'lucide-react';
import { useMachines } from '../../context/PortalContext';
import { macchineService, ModelloAutocomplete } from '../../services/macchineService';
import { useDebounce } from '../../hooks/useDebounce';
import { toast } from 'sonner';

export default function ParcoMacchinePage() {
  const { machines, loading, addMachine, removeMachine } = useMachines();
  const navigate = useNavigate();

  const [showAdd, setShowAdd] = useState(false);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 300);
  const [results, setResults] = useState<ModelloAutocomplete[]>([]);
  const [searching, setSearching] = useState(false);
  const [addingId, setAddingId] = useState<string | null>(null);

  // Ricerca modelli nel catalogo reale (autocomplete pubblico)
  useEffect(() => {
    if (!showAdd || debouncedSearch.trim().length < 2) {
      setResults([]);
      return;
    }
    let cancelled = false;
    setSearching(true);
    macchineService.searchModelli(debouncedSearch)
      .then(r => { if (!cancelled) setResults(r); })
      .catch(() => { if (!cancelled) setResults([]); })
      .finally(() => { if (!cancelled) setSearching(false); });
    return () => { cancelled = true; };
  }, [debouncedSearch, showAdd]);

  const handleAdd = async (modello: ModelloAutocomplete) => {
    setAddingId(modello.id);
    try {
      await addMachine(modello.id);
      setShowAdd(false);
      setSearch('');
      toast.success(`${modello.marca} ${modello.nome} aggiunto`);
    } catch {
      toast.error('Errore durante il salvataggio del modello.');
    } finally {
      setAddingId(null);
    }
  };

  const handleRemove = async (id: string) => {
    try {
      await removeMachine(id);
    } catch {
      toast.error('Errore durante la rimozione del modello.');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16 text-muted-foreground" data-testid="parco-macchine-loading">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Caricamento parco macchine...
      </div>
    );
  }

  return (
    <div data-testid="parco-macchine-page">
      {/* Titolo e azione restavano affiancati con 0px di gap: su 390px il
          pulsante si comprimeva a 114px e la sua etichetta andava a capo. */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">I Miei Modelli</h1>
          <p className="text-muted-foreground text-sm mt-1">Salva le tue stampanti e trova subito i consumabili compatibili.</p>
        </div>
        <button onClick={() => setShowAdd(true)} className="tap h-11 sm:h-9 w-full sm:w-auto flex-none px-4 rounded-lg bg-[hsl(214,58%,41%)] text-white text-sm font-medium flex items-center justify-center gap-1.5 hover:bg-[hsl(214,58%,46%)] transition-colors" data-testid="add-machine-button">
          <Plus className="w-4 h-4" /> Aggiungi Modello
        </button>
      </div>

      {machines.length === 0 && !showAdd ? (
        <div className="rounded-xl border bg-white p-12 text-center" data-testid="parco-macchine-empty">
          <Printer className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-semibold mb-2">Nessun modello salvato</h3>
          <p className="text-sm text-muted-foreground mb-4">Aggiungi le tue stampanti per trovare rapidamente i consumabili compatibili.</p>
          <button onClick={() => setShowAdd(true)} className="tap h-11 sm:h-9 px-4 rounded-lg bg-[hsl(214,58%,41%)] text-white text-sm font-medium">Aggiungi il primo modello</button>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {machines.map(m => (
            <div key={m.id}
              className="rounded-xl border bg-white p-4 cursor-pointer hover:border-[hsl(214,58%,41%)]/40 hover:bg-[hsl(var(--dtk-canvas))] transition-colors group"
              onClick={() => navigate(`/consumabili-per/${m.modelloId}`)}
              title={`Vedi consumabili compatibili con ${m.marca} ${m.nome}`}
              data-testid={`machine-${m.id}`}
            >
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-3 min-w-0">
                  <Printer className="w-5 h-5 text-[hsl(214,58%,41%)] flex-shrink-0" />
                  <div className="min-w-0">
                    <div className="text-sm font-semibold truncate">{m.marca} {m.nome}</div>
                    <div className="text-[10px] text-muted-foreground">{m.tipoMacchinaLabel}</div>
                  </div>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  <button
                    onClick={(e) => { e.stopPropagation(); handleRemove(m.id); }}
                    className="w-7 h-7 rounded flex items-center justify-center text-muted-foreground hover:text-red-500 hover:bg-red-50 transition-colors"
                    data-testid={`remove-machine-${m.id}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                  <ChevronRight className="w-4 h-4 text-muted-foreground group-hover:text-[hsl(214,58%,41%)] transition-colors" />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add Machine Modal */}
      {showAdd && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/40 backdrop-blur-sm" onClick={() => setShowAdd(false)} data-testid="add-machine-modal">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 max-h-[70vh] flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-sm font-semibold">Aggiungi Modello</h3>
              <button onClick={() => setShowAdd(false)} className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-secondary/60" data-testid="close-add-machine-modal">×</button>
            </div>
            <div className="px-4 pt-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <input
                  type="text"
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  placeholder="Cerca marca o modello (es. Kyocera Taskalfa)..."
                  className="w-full h-9 pl-9 pr-3 rounded-lg border text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(var(--dtk-focus))]"
                  autoFocus
                  data-testid="search-machine-input"
                />
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-1">
              {search.trim().length < 2 ? (
                <p className="text-xs text-muted-foreground text-center py-6">Digita almeno 2 caratteri per cercare nel catalogo.</p>
              ) : searching ? (
                <div className="flex items-center justify-center py-6 text-muted-foreground text-sm">
                  <Loader2 className="w-4 h-4 animate-spin mr-2" /> Ricerca...
                </div>
              ) : results.length === 0 ? (
                <p className="text-xs text-muted-foreground text-center py-6">Nessun modello trovato per "{search}".</p>
              ) : (
                results
                  .filter(r => !machines.some(m => m.modelloId === r.id))
                  .map(m => (
                    <button
                      key={m.id}
                      onClick={() => handleAdd(m)}
                      disabled={addingId === m.id}
                      className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-secondary/40 transition-colors text-left disabled:opacity-50"
                      data-testid={`search-result-${m.id}`}
                    >
                      {addingId === m.id ? <Loader2 className="w-4 h-4 text-muted-foreground animate-spin" /> : <Printer className="w-4 h-4 text-muted-foreground" />}
                      <div><div className="text-sm font-medium">{m.marca} {m.nome}</div><div className="text-[10px] text-muted-foreground">{m.tipoMacchinaLabel}</div></div>
                    </button>
                  ))
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
